import { useState } from 'react';
import * as React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import { Box, ScrollContainer, theme } from '@frameio/vapor';
import { FailedItemsDialog } from '../AlertDialog/FailedItemsDialog';
import type { FailedItems, SetFailedItems } from '../JobCard/JobCard';

type TransferSheetProps = {
  renderFooter: () => React.ReactNode;
  renderTransfers: ({
    setFailedItems,
  }: {
    setFailedItems: SetFailedItems;
  }) => React.ReactNode;
  showFooter: boolean;
};

export function TransfersSheetBase({
  renderFooter,
  renderTransfers,
  showFooter,
}: TransferSheetProps) {
  const [failedItems, setFailedItems] = useState<FailedItems>([]);
  return (
    <Container
      // Ignore files dropped in here
      onDragOver={(event) => {
        event.preventDefault();
      }}
      onDrop={(event) => {
        event.preventDefault();
      }}
    >
      <Box flexGrow="1">
        <ScrollContainer>
          <JobCards>
            {renderTransfers({
              setFailedItems,
            })}
          </JobCards>
        </ScrollContainer>
      </Box>

      <AnimatePresence>
        {showFooter && (
          <FooterContainer>
            <motion.div
              animate="in"
              exit="out"
              initial="in"
              transition={{ duration: 0.3 }}
              variants={variants}
            >
              <FooterGradient />
              <StyledFooter>{renderFooter()}</StyledFooter>
            </motion.div>
          </FooterContainer>
        )}
      </AnimatePresence>
      <FailedItemsDialog items={failedItems} variant="uploadFailed" />
    </Container>
  );
}

const FooterGradient = styled.div`
  background-image: ${theme.colors.background.scrollBottom};
  bottom: 80%;
  height: ${theme.spaces[48]};
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: ${theme.zIndices.negative};
`;

const StyledFooter = styled.footer`
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.radii[8]};
  display: block;
`;

const JobCards = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;

  > li + li {
    border-top: 1px solid ${theme.colors.border.secondary};
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;

  position: relative;
  footer {
    background-color: ${theme.colors.background.secondary};
  }
`;

const FooterContainer = styled.div`
  bottom: 0;
  position: sticky;
  z-index: 1;
`;

const variants = {
  out: {
    y: '100%',
    opacity: 0.5,

    // When animating out, take the bottom bar out of document flow so that job
    // cards show up _behind_ the bottom bar (instead of above it), so that
    // there isn't a visible gap above the bottom bar when it slides down.
  } as const,
  in: {
    y: 0,
    opacity: 1,

    // Undo the absolute positioning above, so that the job cards and their
    // scroll bar shows up _above_ the bottom bar.
    transitionEnd: {
      position: 'unset',
    },
  } as const,
};
